import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {Draft} from "../../../../../model/Draft";
import {Item} from "../../../../../model/cart/Item";
import {Product} from "../../../../../model/Product";
import {StartupService} from "../../../../service-ui/startup.service";
import {Router} from "@angular/router";
import {ResourceService} from "../../../../service-data/resource.service";
import {MatDialog} from "@angular/material/dialog";
import {LanguagePipe} from "../../../../pipe/language.pipe";
import {ConfirmDialogComponent} from "../../../confirm-dialog/confirm-dialog.component";
import {Book} from "../../../../../model/Book";
import {BookService} from "../../../../service-data/book.service";
import {Project} from "../../../../../model/Project";
import {MoveProjectComponent} from "../../project/dialogs/move-project/move-project.component";
import {ProjectService} from "../../../../service-data/project.service";
import {ProductService} from "../../../../service-data/product.service";
import {ReasignBookComponent} from "../../project/dialogs/reasign-book/reasign-book.component";
import {modernDialogConfig} from "../../../../service-ui/dialog-config";
import {PaperTypeService} from "../../../../service-data/paper-type.service";
import {ResourceStatus} from "../../../../service-ui/resource-status";
import {Resource} from "../../../../../model/Resource";
import {CoverService} from "../../../../service-data/cover.service";
import {BodyService} from "../../../../service-data/body.service";
import {AlertService} from "../../../../service-ui/alert.service";

@Component({
  selector: 'app-project-book-thumbnail',
  templateUrl: './project-book-thumbnail.component.html',
  styleUrls: ['./project-book-thumbnail.component.css']
})
export class ProjectBookThumbnailComponent {
  @Input() book: Book;
  @Input() product: Product;
  @Input() items: Item[];
  @Output() selectedItem = new EventEmitter<Project | Draft | Book>();
  @Output() editingDetailsChange = new EventEmitter<string>();

  displayScale = 1.0;
  editingTitle = false;

  /** The inline title editor, present only while 'editingTitle'. */
  @ViewChild('titleInput') titleInput;


  Math = Math;
  private requestedResources = new Set<number>();

  constructor(public router: Router, public startupService: StartupService, private resourceService: ResourceService, private bookService: BookService, private projectService: ProjectService, private productService: ProductService, public paperTypeService: PaperTypeService, private coverService: CoverService, private bodyService: BodyService, private alertService: AlertService, public dialog: MatDialog) {
  }

  // ── There is deliberately no image code in this component ─────────────
  //
  // The visible thumbnail is rendered by the child
  // `<app-project-book-cover-image>`, which owns the request, the artefact-
  // stage decision, the not-standardized fallback, and the COVER / BODY /
  // RESOURCE subscriptions that refresh it.
  //
  // This class used to carry a second, independent copy of that logic driven
  // by `@ViewChild('coverImage')` — but the template has no `#coverImage`, so
  // the ref was always undefined, every render call was a no-op or a throw,
  // and the whole block had been dead for as long as the current template has
  // existed. It was removed on 2026-09-15 rather than repaired: two
  // implementations racing for one `<img>` is how the cache-id collisions in
  // this area started. **If a thumbnail is not updating, the fix belongs in
  // `ProjectBookCoverImageComponent`.**


  /**
   * Emit the Book itself, not `book.project`.
   *
   * The parent stores the Project for the grid, but the detail pane used to
   * render `selectedItem.primaryBook` - so opening any non-primary tile
   * silently showed the primary Book instead of the one that was clicked.
   * `ProjectsComponent.selectedItemChanged` unpacks the union.
   */
  open(book: Book) {
    this.selectedItem.emit(book);
    this.editingDetailsChange.emit('BOOK');
  }

  makePrimary(book: Book, element: any ) {
    element.classList.add('spinner');
    this.projectService.setBookAsPrimary(book.project.ID, book.ID).subscribe( x => {
      element.classList.remove('spinner');
    });
  }

  /**
   * "Make Draft Version of this Book" - Book -> Draft.
   *
   * Must be makeBookIntoDraft, not makeDraftIntoBook. This used to call the
   * Draft->Book endpoint with a Book id, so the server looked the id up in the
   * `draft` table, where it either missed entirely or - worse - hit an
   * unrelated Draft that happened to share the number and promoted *that* into
   * a Book. ClassicProjectThumbnailComponent always called the right one, so
   * the same menu entry behaved differently depending on which screen it was
   * pressed from.
   */
  makeDraft(book: Book, element: any ) {
    element.classList.add('spinner');
    this.projectService.makeBookIntoDraft(book.ID, false).subscribe( x => {
      element.classList.remove('spinner');
    });
  }

  // copy(book: Book, element: any ) {
  //   element.classList.add('spinner');
  //   this.bookService.copy(book.ID).subscribe( x => {
  //     element.classList.remove('spinner');
  //   });
  // }

  archive (book: Book, element: any ): void {
    // The spinner goes on when the archive actually starts, not here: cancelling
    // the confirm dialog used to leave the menu item spinning forever.
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: {
        name: book.title,
        title: LanguagePipe.get('Archive', 'compressSpaces') + ' ' + book.title + LanguagePipe.get('?', 'compressSpaces'),
        noButton: LanguagePipe.get('Cancel', 'compressSpaces'),
        yesButton: LanguagePipe.get('Archive', 'compressSpaces') + ' ' + book.title,
        returnValue: 'ok'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed. result[' + result + ']');
      if ( result && result === 'ok' ) {
        element.classList.add('spinner');
        this.bookService.archive(book.ID).subscribe( proj => {
          element.classList.remove('spinner');
          console.log('Archived a book');
        });
      }
    });

  }
  /**
   * "Create New Project With Book".
   *
   * Must be createNewProjectWithBook, not ...WithDraft - the same
   * id-against-the-wrong-table hazard described on makeDraft above. This is
   * the identical defect that was found and fixed in
   * ClassicProjectThumbnailComponent; the fix was never ported here.
   */
  createNewProjectWithBook(book: Book, element: any ) {
    element.classList.add('spinner');
    this.projectService.createNewProjectWithBook(book.ID, book.title).subscribe( x => {
      element.classList.remove('spinner');
    });
  }

  changeProject(book: Book, element: any ) {
    // modernDialogConfig, not `new MatDialogConfig()` — see
    // service-ui/dialog-config.ts: a constructed config drops the
    // app-wide `modern-dialog` skin.
    const dialogConfig = modernDialogConfig({ width: '640px' });
    dialogConfig.data = { book: book, projects: this.projectService.objects, folders: this.startupService.getCurrentOrganization().getProjectFolders() };
    const dialogRef = this.dialog.open(ReasignBookComponent, dialogConfig);
  }

  moveProject(project: Project, element: any ) {
    const dialogConfig = modernDialogConfig();
    dialogConfig.width = '640px';
    dialogConfig.disableClose = false;
    dialogConfig.data = { organization: this.startupService.getCurrentOrganization(), project: project };
    const dialogRef = this.dialog.open(MoveProjectComponent, dialogConfig);
  }

  /**
   * A Book is ordered through its Product, so a Book with no Product row
   * cannot be added to the cart at all. That case used to fall out of the loop
   * and do nothing: the menu closed, the cart did not change, and nothing said
   * why. Tell the operator rather than failing silently.
   */
  addToCart(book: Book, element: any ) {
    for (const product of this.productService.objects ) {
      if ( product.book_ID === book.ID) {
        const item: Item = this.startupService.getCart().addProductItem(product, 1);
        return;
      }
    }
    this.alertService.error(LanguagePipe.get('No product found for this book', 'compressSpaces'));
  }

  // ── Inline title editing ──
  /**
   * Rename from the overflow menu.
   *
   * Focus has to be applied in a setTimeout: the input does not exist
   * until Angular has re-rendered the *ngIf this flag controls.
   *
   * 'startEditTitle' finds the same input by walking up from its own
   * event target, which a menu click cannot do — mat-menu renders its
   * panel in an overlay at the end of the body, nowhere near
   * '.pt-title-bar'. Hence the ViewChild.
   */
  startRename(): void {
    this.editingTitle = true;
    setTimeout(() => {
      const input = this.titleInput && this.titleInput.nativeElement;
      if ( input ) { input.focus(); input.select(); }
    });
  }

  startEditTitle(event: Event) {
    event.stopPropagation();
    this.editingTitle = true;
    setTimeout(() => {
      const input = (event.target as HTMLElement).closest('.pt-title-bar')?.querySelector('.pt-title-input') as HTMLInputElement;
      if (input) { input.focus(); input.select(); }
    });
  }

  commitTitle(value: string) {
    const trimmed = value.trim();
    this.editingTitle = false;
    if (trimmed && trimmed !== this.book.title) {
      this.book.title = trimmed;
      this.bookService.updateItem(this.book).subscribe();
    }
  }

  cancelTitle(input: HTMLInputElement) {
    input.value = this.book.title || '';
    this.editingTitle = false;
  }

  // ── Helper methods (matching ClassicProjectThumbnail) ──
  getDaysOldString(date: Date | string): string {
    const days = this.getDaysOld(date);
    if (days > 365) {
      const y = Math.floor(days / 365);
      const d = days % 365;
      return y + 'y ' + d + 'd';
    }
    return days + 'd';
  }

  getDaysOld(date: Date | string): number {
    if (!date) { return 0; }
    return Math.floor((new Date().getTime() - new Date(date).getTime()) / (1000 * 60 * 60 * 24));
  }

  formatTrim(width: number, height: number): string {
    if (!width || !height) { return ''; }
    return this.localizeNumber((Math.round((width / 72) * 100) / 100)) + 'x' + this.localizeNumber((Math.round((height / 72) * 100) / 100));
  }

  formatShift(shift: number): string {
    if (shift == null || shift === undefined) { return this.localizeNumber('0.00'); }
    return this.localizeNumber((Math.round((shift / 72) * 100) / 100).toFixed(2));
  }

  formatScale(scale: number): string {
    if (scale == null || scale === undefined) { return this.localizeNumber('100') + '%'; }
    return this.localizeNumber((Math.round(scale * 100))) + '%';
  }

  localizeNumber(value: any): string {
    return LanguagePipe.localizeNumber(value);
  }

  // --- Resource id badge -------------------------------------------
  // Takes the SIDE object (book.cover / book.body) rather than a bare
  // resource_ID so `standardized` is reachable. Same signature as the
  // Draft thumbnail, which passes a side that simply has no
  // `standardized` field. See src/app/service-ui/resource-status.ts.

  getResourceStatusColor(side: any): string {
    return ResourceStatus.fill(!!(side && side.resource_ID), this.resourceFor(side && side.resource_ID));
  }

  /** Bottom stripe encoding `standardized`; blank until prepared is COMPLETE. */
  getResourceStripe(side: any): string {
    return ResourceStatus.stripe(this.resourceFor(side && side.resource_ID), side && side.standardized);
  }

  isResourceActionable(side: any): boolean {
    return ResourceStatus.isActionable(this.resourceFor(side && side.resource_ID), side && side.standardized);
  }

  getResourceDisplayString(side: any): string {
    return ResourceStatus.displayString(side && side.resource_ID);
  }

  getResourceHoverText(side: any, label: string): string {
    return ResourceStatus.hoverText(label, !!(side && side.resource_ID),
      this.resourceFor(side && side.resource_ID), side && side.standardized);
  }

  /**
   * Advance whichever stage this side is actually ready for.
   *
   * `ResourceStatus.action` decides, so the ordering rule (never ask for
   * standardization before preparation is COMPLETE) lives in one place
   * rather than being re-derived per component.
   *
   * `isCover` selects the service because Cover and Body are separate
   * entities with separate endpoints - there is no shared base the two
   * can be updated through.
   */
  onResourceBadgeClick(side: any, isCover: boolean, event: Event): void {
    if (event) { event.stopPropagation(); }
    if (!side) { return; }
    const res = this.resourceFor(side.resource_ID);
    const stage = ResourceStatus.action(res, side.standardized);
    if (stage === 'PREPARE') {
      res.prepared = 'WAITING';
      this.resourceService.updateItem(res).subscribe();
    } else if (stage === 'STANDARDIZE') {
      side.standardized = 'WAITING';
      if (isCover) {
        this.coverService.updateItem(side).subscribe();
      } else {
        this.bodyService.updateItem(side).subscribe();
      }
    }
  }

  /**
   * Resolve a Resource, fetching it once if it is not loaded.
   *
   * ResourceService is not loaded at startup, so the row usually is not
   * there on first paint. `requestedResources` is what stops that
   * becoming a request storm: this is called from template getters,
   * re-evaluated on every change-detection pass, so an unguarded
   * `getItem()` would fire one HTTP call per tick until the row landed.
   */
  private resourceFor(resource_ID: number): Resource {
    if (!resource_ID) {
      return null;
    }
    const res = this.resourceService.objects.find(r => r.ID === resource_ID);
    if (!res) {
      if (!this.requestedResources.has(resource_ID)) {
        this.requestedResources.add(resource_ID);
        this.resourceService.getItem(resource_ID).subscribe();
      }
      return null;
    }
    return res;
  }

  getPaperTypeName(paperType_ID: number): string {
    if (!paperType_ID) { return ''; }
    const pt = this.paperTypeService.objects.find(p => p.ID === paperType_ID);
    if (pt) { return LanguagePipe.get(pt.name, 'compressSpaces'); }
    return 'P:' + paperType_ID;
  }

  getLanguageString(value: string): string {
    if (!value) { return ''; }
    return LanguagePipe.get(value, 'compressSpaces');
  }

}
